import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { surveysApi } from '../features/surveys/api.ts';
import { useAuthStore } from '../store/auth.ts';

export function Surveys(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.isOrgAdmin ?? false;

  const { data, isLoading } = useQuery({ queryKey: ['surveys'], queryFn: () => surveysApi.list() });

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(false);

  const create = useMutation({
    mutationFn: () => surveysApi.create({ title: title.trim(), description: description.trim() || undefined }),
    onSuccess: (d) => {
      setShowForm(false); setTitle(''); setDescription(''); setError(false);
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void navigate({ to: '/surveys/$surveyId', params: { surveyId: d.id } });
    },
    onError: () => setError(true),
  });

  const list = data ?? [];

  function statusLabel(status: string): string {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'CLOSED') return 'Closed';
    return 'Draft';
  }

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl tracking-tight">Surveys</h1>
        {isAdmin && (
          <button onClick={() => { setShowForm((v) => !v); setError(false); }} className="btn-primary">
            <Icon name="add" size={15} /> New survey
          </button>
        )}
      </header>

      {showForm && (
        <section className="card-paper space-y-3 p-4">
          <h2 className="status-label text-muted-foreground">New survey</h2>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); setError(false); create.mutate(); }}
          >
            <div className="space-y-1">
              <label className="field-label">Survey title</label>
              <input required className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="field-label">Description</label>
              <textarea className="input min-h-[4rem]" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            {error && <p className="text-sm text-destructive" role="alert">Something went wrong. Please try again.</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={create.isPending} className="btn-primary">
                {create.isPending ? 'Saving…' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </section>
      )}

      {isLoading ? (
        <p className="status-label text-muted-foreground" role="status">Loading…</p>
      ) : list.length === 0 ? (
        <p className="card-paper p-6 text-center text-sm text-muted-foreground">No surveys yet</p>
      ) : (
        <ul className="space-y-2">
          {list.map((s) => (
            <li key={s.id} className="card-paper flex items-center justify-between gap-3 p-4">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Icon name="assignment" size={20} className="shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="font-medium leading-snug truncate">{s.title}</p>
                  <p className="mono-meta text-muted-foreground">{statusLabel(s.status)} · {s.responseCount} responses</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isAdmin && (
                  <>
                    <Link to="/surveys/$surveyId" params={{ surveyId: s.id }} className="btn-ghost px-2 py-1 text-sm">Edit</Link>
                    <Link to="/surveys/$surveyId/results" params={{ surveyId: s.id }} className="btn-ghost px-2 py-1 text-sm">Results</Link>
                  </>
                )}
                <Link to="/surveys/$surveyId/collect" params={{ surveyId: s.id }} className="btn-primary px-2 py-1 text-sm">Collect</Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
